import React, { useCallback, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import DynamicTable from '@atlaskit/dynamic-table';
import type { HeadType, RowType } from '@atlaskit/dynamic-table/types';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { BoardsTable$key } from '@atlassian/jira-relay/src/__generated__/BoardsTable.graphql';
import { Link as RouterLink } from '@atlassian/react-resource-router';
import { BOARDS_ITEM_TYPE } from '@atlassian/jira-favourite-change-provider/src/model/constants.tsx';
import { BOARDS_TAB_KEY, SUCCESS_STATE } from '../../../../common/constants/tab-content.tsx';
import {
	EndInteractionsTracker,
	useFireErrorOrSuccessAnalytics,
} from '../../../../common/utils.tsx';
import {
	JIRA_SEARCH_RESULT_PROJECT_CONTAINER,
	JIRA_SEARCH_RESULT_USER_CONTAINER,
	type AnyBoard,
	type ProjectBoard,
	type UserBoard,
} from '../types.tsx';
import { FavoriteButtonWithSSR } from '../../../../common/ui/favourite-button/index.tsx';
import { CellWithAvatar } from './cell-with-avatar/index.tsx';
import messages from './messages.tsx';

type BoardsViewProps = {
	data: BoardsTable$key;
	itemsPerPage: number;
};

const isProjectBoard = (board: AnyBoard): board is ProjectBoard =>
	board.container?.__typename === JIRA_SEARCH_RESULT_PROJECT_CONTAINER;

export const BoardsTable = ({ data, itemsPerPage }: BoardsViewProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	useFireErrorOrSuccessAnalytics(!!data, null, false, BOARDS_TAB_KEY);

	const rowData = useFragment<BoardsTable$key>(
		graphql`
			fragment BoardsTable on SearchItemConnection {
				edges {
					node {
						__typename
						title
						url
						... on SearchResultJiraBoard {
							boardId
							favourite
							iconUrl
							container {
								__typename
								... on SearchResultJiraBoardProjectContainer {
									projectId
									key: projectKey
									name: projectName
								}
								... on SearchResultJiraBoardUserContainer {
									userAccountId
									name: userName
								}
							}
						}
					}
				}
			}
		`,
		data,
	);

	const onBoardLinkClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
			}),
			'boardItem',
			{ pageLocation: 'home' },
		);
	}, [createAnalyticsEvent]);

	const tableHeaders: HeadType = useMemo(
		() => ({
			cells: [
				{
					key: 'starred',
					width: 5,
					content: (
						<Flex xcss={tableHeaderIconWrapperStyles}>
							<StarStarredIcon
								label={formatMessage(messages.starredColumnHeader)}
								color={token('color.icon.accent.yellow')}
							/>
						</Flex>
					),
					isSortable: false,
				},
				{
					key: 'name',
					width: 40,
					content: formatMessage(messages.nameColumnHeader),
					isSortable: true,
				},
				{
					key: 'location',
					width: 50,
					content: formatMessage(messages.locationColumnHeader),
					isSortable: true,
				},
				// TODO: Add actions menu (https://jplat.atlassian.net/browse/BLU-3709)
			],
		}),
		[formatMessage],
	);

	const tableRows: RowType[] = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const projectAndUserBoards = (rowData?.edges
			.map((edge) => edge.node)
			.filter((board) => {
				if (!board?.boardId) return false;

				return (
					board?.container?.__typename === JIRA_SEARCH_RESULT_PROJECT_CONTAINER ||
					board?.container?.__typename === JIRA_SEARCH_RESULT_USER_CONTAINER
				);
			}) ?? []) as (ProjectBoard | UserBoard)[];

		return projectAndUserBoards.map((board, index) => {
			const containerKey = isProjectBoard(board)
				? board.container.projectId
				: board.container.userAccountId;

			const containerLabel = isProjectBoard(board)
				? `${board.container.name} (${board.container.key})`
				: board.container.name;

			const containerAvatarShape = isProjectBoard(board) ? 'square' : 'circle';

			return {
				key: `row-${index}`,
				cells: [
					{
						key: `starred-${board?.favourite?.toString()}`,
						content: (
							<FavoriteButtonWithSSR
								itemId={board?.boardId ?? ''}
								itemType={BOARDS_ITEM_TYPE}
								isFavoriteInitial={Boolean(board?.favourite)}
							/>
						),
					},
					{
						key: `${board?.title}`,
						content: (
							<RouterLink href={board?.url} onClick={onBoardLinkClick}>
								{board?.title}
							</RouterLink>
						),
					},
					{
						key: `${containerLabel}`,
						content: (
							<CellWithAvatar
								key={containerKey}
								imageUrl={board?.iconUrl ?? ''}
								appearance={containerAvatarShape}
								text={containerLabel}
							/>
						),
					},
					// TODO: Add actions menu (https://jplat.atlassian.net/browse/BLU-3709)
				],
			};
		});
	}, [rowData?.edges, onBoardLinkClick]);

	return (
		<>
			<DynamicTable
				head={tableHeaders}
				rows={tableRows}
				rowsPerPage={itemsPerPage}
				defaultSortKey="name"
				defaultSortOrder="ASC"
			/>
			<EndInteractionsTracker transitionKey={BOARDS_TAB_KEY} renderState={SUCCESS_STATE} />
		</>
	);
};

const tableHeaderIconWrapperStyles = xcss({
	paddingInlineStart: 'space.100',
});
